import React, { useState, useCallback, useEffect } from 'react';
import type { FC } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';

import { CreativeAPIs } from '@/api';
import { useCreateExportMutation, useCreativeQuery } from '@/api/queries';
import type { CreativeDetails } from '@/api/types';
import type { Export } from '@/api/types/export';
import { Button, ButtonSize, ButtonType } from '@/common/button';
import { CustomToast } from '@/common/custom-toast';
import { ExportCreativeModal } from '@/common/export-creative-modal';
import { IntaglioIcon } from '@/common/intaglio-icon';
import { Navbar, NavbarType } from '@/common/navbar';

import { ImageDownloadModal } from './image-download-modal';

export const CreativeAssets: FC = () => {
    const params = useParams<{ creativeId: string }>();
    const creativeId = Number.parseInt(params.creativeId || '');
    if (Number.isNaN(creativeId)) {
        console.error('Invalid params ID in url params:', params.creativeId);
        return <Navigate to="/creatives" replace={true} />;
    }

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [exports, setExports] = useState<Export[]>([]);
    const [currentImageSrc, setCurrentImageSrc] = useState<string | null>(null);
    const [creative, setCreative] = useState<CreativeDetails>();
    const [exportCount, setExportCount] = useState<number>(0);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const navigate = useNavigate();

    const { mutate: createExport } = useCreateExportMutation();

    const fetchCreativesExports = useCallback(async () => {
        setIsLoading(true);
        setExports(await CreativeAPIs.getExports(creativeId));
        setIsLoading(false);
    }, [creativeId]);

    if (!creativeId) {
    }

    const { refetch: refetchCreative } = useCreativeQuery(creativeId, {
        onSuccess: async (creative: CreativeDetails) => {
            setCreative(creative);
            setExportCount((creative?.contentVariants.length || 0) * (creative?.creativeVariants.length || 0));
        },
        staleTime: Number.POSITIVE_INFINITY,
        cacheTime: 0,
    });

    useEffect(() => {
        fetchCreativesExports();
    }, [fetchCreativesExports]);

    const handleExport = (exportFormats: string[]): void => {
        createExport(
            { creative: creativeId, formats: exportFormats },
            {
                onSuccess: () => {
                    CustomToast.success(
                        "Export has been requested. You will receive an email with the download link once it's ready.",
                    );
                },
            },
        );
    };

    return (
        <div className="flex flex-col h-screen w-screen bg-black-50">
            <Navbar
                type={NavbarType.LIGHT}
                leading={
                    <Link to="/">
                        <IntaglioIcon />
                    </Link>
                }
                center={<p className="text-base text-white">Untitled Creative</p>}
                trailing={
                    <Button
                        title={`Export ${exportCount} Asset${exportCount === 1 ? '' : 's'}`}
                        buttonType={ButtonType.PRIMARY}
                        buttonSize={ButtonSize.LARGE}
                        onClick={() => setIsExportModalOpen(true)}
                    />
                }
            />
            <div className="z-10 -mt-3 h-14 w-full flex flex-col items-center">
                <div className="flex flex-row gap-4 bg-[#FFD08A] rounded-full py-3 px-8 items-center">
                    <p className="text-sm">Opened in view only mode.</p>
                    <button
                        type="button"
                        className="bg-[#FFE6C8] px-3 py-1 rounded-md text-sm"
                        onClick={() => {
                            navigate(`/creatives/generate/${creativeId}`);
                        }}
                    >
                        Edit Content
                    </button>
                </div>
            </div>
            <div className="overflow-y-auto p-5 space-y-8 w-full">
                {exports.map((exportItem, index) => (
                    <div key={exportItem.id}>
                        <h2 className="text-base font-semibold mb-4 text-white">Version {exports.length - index}</h2>
                        <div className="flex flex-row gap-4 overflow-x-auto no-scrollbar">
                            {exportItem.exportedAssets?.map((asset, index) => (
                                <div
                                    key={asset.id}
                                    className="flex flex-col max-w-[42vw] aspect-square"
                                    onClick={() => setCurrentImageSrc(asset.file)}
                                    onKeyUp={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            setCurrentImageSrc(asset.file);
                                        }
                                    }}
                                >
                                    <div className="flex flex-row justify-between items-center mb-2 w-[24vw]">
                                        <div className="flex flex-row gap-2 items-center">
                                            <p className="text-xs text-white truncate max-w-[15vw]">{asset.name}</p>
                                            <p className="text-xs text-gray-60 flex flex-row gap-2 items-center">
                                                <span className="text-xl">·</span>
                                                {asset.format}
                                            </p>
                                        </div>
                                        <div className="text-xs text-gray-60">
                                            {asset.width} x {asset.height}
                                        </div>
                                    </div>
                                    <div className="flex flex-row justify-center p-1 w-[24vw] aspect-square bg-gray-200 rounded-md overflow-hidden bg-gray-150 chess">
                                        <img src={asset.file} alt="Asset" className="object-contain" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            <ImageDownloadModal
                src={currentImageSrc ?? ''}
                isOpen={!!currentImageSrc}
                setIsOpen={(isOpen) => {
                    if (!isOpen) {
                        setCurrentImageSrc(null);
                    }
                }}
            />

            <ExportCreativeModal
                isOpen={isExportModalOpen}
                setIsOpen={setIsExportModalOpen}
                exportCount={(creative?.contentVariants.length || 0) * (creative?.creativeVariants.length || 0)}
                onConfirm={handleExport}
            />
        </div>
    );
};
